import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { BaseFile } from 'config'
import { useEffect, useRef, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, ButtonGroup } from 'stories/components'
import { capitalizeFirstLetter, InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { BrokerAgreementPlaceholders, brokerOptions } from '../BrokerAgreement/constants'
import { defaultInputs } from './constants'

export interface IBrokerEmailConfig {
  subject: string
  content: string
  files: BaseFile[]
}

export const BrokerEmails = () => {
  const [inputs, setInputs] = useState(defaultInputs())
  const [isLoading, setLoading] = useState(true)
  const [template, setTemplate] = useState<string>()
  const [config, setConfig] = useState<Record<string, IBrokerEmailConfig>>()
  const [isChanged, setChanged] = useState(false)
  const [cursorPos, setCursorPos] = useState(0)
  const [focusField, setFocusField] = useState<Record<string, any>>({ type: '', index: 0 })

  const activeInputRef = useRef(null)

  useEffect(() => {
    init()
  }, [])

  const init = async () => {
    const config = await getAdminConfig('brokerEmails')
    setConfig(config)
    setTemplate('broker')
    setLoading(false)
  }

  useEffect(() => {
    if (!template || !config) return

    const brokerConfig = config[template]
    const newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = (brokerConfig as any)[key]
    })
    setInputs(newInputs)
  }, [template])

  const onChangeTemplate = async (value: string) => {
    setTemplate(value)
  }

  const onChange = (key: string, value: string) => {
    if (!config || !template) return

    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
    setChanged(true)

    const newConfig = cloneDeep(config)
    ;(newConfig[template] as any)[key] = newInputs[key].value
    setConfig(newConfig)
  }

  const onSubmit = async () => {
    if (!config) return

    setLoading(true)
    await setAdminConfig('brokerEmails', config)
    setLoading(false)
    setChanged(false)
  }

  const handleFocus = (event: any, type: string) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart)
    setFocusField({ type })
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart)
  }

  const insertTextAtCursor = (text: string) => {
    const input: any = activeInputRef.current
    if (!input) return

    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length

    const { type } = focusField
    onChange(type, newValue)

    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  return (
    <div className={`relative `}>
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />
      <h2 className={`text-2xl font-bold flex items-center mb-4`}>Third Party Email Templates</h2>
      <div className="mb-6">
        <p className="ml-3 text-lg font-medium mb-2">- Placeholders</p>
        <div className="p-3 border rounded-md">
          <div className="flex gap-x-1 gap-y-3 flex-wrap justify-between">
            {BrokerAgreementPlaceholders.sort().map((item, index) => {
              return (
                <pre
                  key={index}
                  className="w-[240px] cursor-pointer"
                  onClick={() => insertTextAtCursor(`{{${capitalizeFirstLetter(item)}}}`)}
                >
                  {capitalizeFirstLetter(item)}
                </pre>
              )
            })}
          </div>
        </div>
      </div>
      <div className={`flex items-center justify-between mb-4`}>
        <ButtonGroup title={brokerOptions()} value={template} onChange={(v) => onChangeTemplate(v)} />
      </div>

      <div className="mb-6 relative">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          return (
            <div className={`input ${input.span ? 'col-span-full' : 'col-span-1'}`} key={index}>
              <RenderInput
                input={input}
                Key={key}
                onChange={onChange}
                onFocus={(e: any) => key !== 'files' && handleFocus(e, key)}
                onKeyUp={handleCursorChange}
                onClick={handleCursorChange}
              />
            </div>
          )
        })}
      </div>

      <div className="flex justify-center mt-4">
        <Button onClick={onSubmit} loading={isLoading}>
          Save
        </Button>
      </div>
    </div>
  )
}
